import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  Plus,
  AlertTriangle,
  ArrowRight,
  Settings
} from 'lucide-react';
import GoalCard from './GoalCard';
import { goalService } from '../../services/goalService';

/**
 * GoalsWidget - Dashboard widget for displaying goals
 *
 * Props:
 * - viewType: 'my' | 'firm'
 * - onCreateGoal: function() - Called when create button is clicked
 * - maxGoals: number - Maximum goals to display
 */
const GoalsWidget = ({
  viewType = 'my',
  onCreateGoal,
  maxGoals = 3,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load both settings and dashboard data
      const [settingsRes, dashboardRes] = await Promise.all([
        goalService.getUserSettings(),
        goalService.getDashboardGoals(viewType),
      ]);

      setSettings(settingsRes.data);
      setData(dashboardRes.data);
    } catch (err) {
      console.error('Error loading goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // If goals are disabled in settings, don't render
  if (settings && !settings.show_on_dashboard) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 bg-gray-200 rounded-xl animate-pulse" />
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="text-center py-4">
          <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 text-sm text-yellow-600 hover:text-yellow-700 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const goals = data?.goals || [];
  const hasGoals = goals.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-xl">
              <Target className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {viewType === 'my' ? 'My Goals' : 'Firm Goals'}
              </h3>
              <p className="text-xs text-gray-500">
                {hasGoals
                  ? `${data.on_track_count} on track, ${data.at_risk_count} need attention`
                  : 'Set goals to track your progress'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {onCreateGoal && (
              <button
                onClick={onCreateGoal}
                className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-lg transition-colors"
                title="Create Goal"
              >
                <Plus className="h-5 w-5" />
              </button>
            )}
            <Link
              to="/goals"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Goal Settings"
            >
              <Settings className="h-5 w-5" />
            </Link>
          </div>
        </div>

        {/* Summary Stats */}
        {hasGoals && (
          <div className="flex items-center space-x-4 mt-3">
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-gray-600">
                {data.on_track_count} On Track
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="text-xs text-gray-600">
                {data.at_risk_count} At Risk
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-xs text-gray-600">
                {data.achieved_count} Achieved
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {!hasGoals ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <Target className="h-8 w-8 text-yellow-600" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-1">
              No {viewType === 'my' ? 'Personal' : 'Firm'} Goals Yet
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Set measurable goals to track your progress and stay motivated.
            </p>
            {onCreateGoal && (
              <button
                onClick={onCreateGoal}
                className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors font-medium text-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Goal
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {goals.slice(0, maxGoals).map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                compact={true}
                showActions={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {hasGoals && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <Link
            to="/goals"
            className="flex items-center justify-center space-x-1 text-sm text-yellow-600 hover:text-yellow-700 font-medium transition-colors"
          >
            <span>View All Goals</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default GoalsWidget;
